import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { authAxios } from "@/utils/auth";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Save, Trash2, AlertTriangle, Calendar, Database, Clock, Globe, HardDrive, FileSearch, Loader2, UserPlus, Lock, Coins, Gift, Infinity as InfinityIcon } from "lucide-react";
import { toast } from "sonner";
import { TIMEZONES, getTimezoneLabel } from "@/utils/timezone";
import MigrationsCard from "./MigrationsCard";

export default function AdminSettings() {
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingTimezone, setSavingTimezone] = useState(false);
  const [settings, setSettings] = useState({
    default_theme: "ancient_rome",
    data_source_max_size: 0,
    prompt_logging_enabled: true,
    minimal_core_data: false,
    style_card_max_token_size: 5000,
  });
  
  // Registration settings state
  const [registrationSettings, setRegistrationSettings] = useState({
    registration_mode: "yes",
    signup_bonus_coins: 0,
    verify_email_bonus: 500,
    email_limited_description: "",
    approved_emails: [],
    latest_tc_version: "1.0",
    force_tc_update: false,
  });
  const [savingRegistration, setSavingRegistration] = useState(false);
  const [approvedEmailsText, setApprovedEmailsText] = useState("");
  
  // User timezone preference
  const [userTimezone, setUserTimezone] = useState(user?.timezone || "UTC");
  
  // Prompt log management state
  const [promptLogStats, setPromptLogStats] = useState(null);
  const [clearType, setClearType] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [clearing, setClearing] = useState(false);
  
  // Base64 cleanup state
  const [base64Report, setBase64Report] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [cleaningBase64, setCleaningBase64] = useState(false);

  // System-wide Subscribe Bonus offer state
  const [systemSubOffer, setSystemSubOffer] = useState({
    bonus_tokens: 0,
    max_uses: -1,
    use_count: 0,
    is_active: false,
    welcome_message: "",
    plan_bonuses: { monthly: {}, yearly: {} },
  });
  const [systemSubBonusInput, setSystemSubBonusInput] = useState("0");
  const [systemSubMaxUsesInput, setSystemSubMaxUsesInput] = useState("-1");
  const [savingSystemSub, setSavingSystemSub] = useState(false);
  const [systemSubPreview, setSystemSubPreview] = useState({ monthly: {}, yearly: {} });

  useEffect(() => {
    fetchSettings();
    fetchPromptLogStats();
    fetchRegistrationSettings();
    fetchSystemSubOffer();
  }, []);
  
  // Update local timezone when user changes
  useEffect(() => {
    if (user?.timezone) {
      setUserTimezone(user.timezone);
    }
  }, [user?.timezone]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings`, {
        withCredentials: true,
      });
      setSettings({
        default_theme: response.data.default_theme || "ancient_rome",
        data_source_max_size: response.data.data_source_max_size || 0,
        prompt_logging_enabled: response.data.prompt_logging_enabled !== false,
        minimal_core_data: response.data.minimal_core_data === true,
        style_card_max_token_size:
          typeof response.data.style_card_max_token_size === "number"
            ? response.data.style_card_max_token_size
            : 5000,
      });
      setLoading(false);
    } catch (error) {
      console.error("Failed to fetch settings:", error);
      setLoading(false);
    }
  };

  const fetchPromptLogStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/prompt-logs/stats`, {
        withCredentials: true,
      });
      setPromptLogStats(response.data);
    } catch (error) {
      console.error("Failed to fetch prompt log stats:", error);
    }
  };

  const fetchRegistrationSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/registration-settings`, {
        withCredentials: true,
      });
      setRegistrationSettings(response.data);
      // Convert approved emails array to comma-separated text
      setApprovedEmailsText((response.data.approved_emails || []).join(", "));
    } catch (error) {
      console.error("Failed to fetch registration settings:", error);
    }
  };

  const handleSaveRegistrationSettings = async () => {
    setSavingRegistration(true);
    try {
      // Parse comma-separated emails
      const emailsArray = approvedEmailsText
        .split(",")
        .map(e => e.trim())
        .filter(e => e.length > 0);
      
      await axios.put(`${API}/admin/registration-settings`, {
        ...registrationSettings,
        approved_emails: emailsArray
      }, {
        withCredentials: true,
      });
      toast.success("Registration settings updated");
      fetchRegistrationSettings();
    } catch (error) {
      console.error("Failed to save registration settings:", error);
      toast.error(error.response?.data?.detail || "Failed to save settings");
    } finally {
      setSavingRegistration(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, settings, {
        withCredentials: true,
      });
      toast.success("Settings updated successfully");
    } catch (error) {
      console.error("Failed to save settings:", error);
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTimezone = async (newTimezone) => {
    setSavingTimezone(true);
    try {
      await authAxios.put("/profile", { timezone: newTimezone });
      setUserTimezone(newTimezone);
      // Refresh user context to update timezone globally
      if (refreshUser) {
        await refreshUser();
      }
      toast.success(`Timezone updated to ${getTimezoneLabel(newTimezone)}`);
    } catch (error) {
      console.error("Failed to save timezone:", error);
      toast.error("Failed to save timezone preference");
    } finally {
      setSavingTimezone(false);
    }
  };

  const handleClearPromptLogs = async () => {
    const confirmMsg = clearType === "all" 
      ? "Are you sure you want to delete ALL prompt logs? This cannot be undone."
      : `Are you sure you want to delete prompt logs from ${startDate} to ${endDate}?`;
    
    if (!window.confirm(confirmMsg)) return;
    
    setClearing(true);
    try {
      const payload = { clear_type: clearType };
      if (clearType === "date_range") {
        if (!startDate || !endDate) {
          toast.error("Please select both start and end dates");
          setClearing(false);
          return;
        }
        payload.start_date = startDate;
        payload.end_date = endDate;
      }
      
      const response = await axios.delete(`${API}/admin/prompt-logs/clear`, {
        data: payload,
        withCredentials: true,
      });
      
      toast.success(`Deleted ${response.data.deleted_count} prompt logs`);
      fetchPromptLogStats(); // Refresh stats
    } catch (error) {
      console.error("Failed to clear prompt logs:", error);
      toast.error(error.response?.data?.detail || "Failed to clear prompt logs");
    } finally {
      setClearing(false);
    }
  };

  const fetchBase64Report = async () => {
    setLoadingReport(true);
    try {
      const response = await axios.get(`${API}/matches/base64-report`, {
        withCredentials: true,
      });
      setBase64Report(response.data);
    } catch (error) {
      console.error("Failed to fetch base64 report:", error);
      toast.error(error.response?.data?.detail || "Failed to fetch report");
    } finally {
      setLoadingReport(false);
    }
  };

  const handleCleanupBase64 = async () => {
    if (!window.confirm("Are you sure you want to remove all legacy base64 data from matches? This cannot be undone.")) {
      return;
    }
    
    setCleaningBase64(true);
    try {
      const response = await axios.post(`${API}/matches/cleanup-base64`, {}, {
        withCredentials: true,
      });
      
      const { stats, mb_freed } = response.data;
      const details = [];
      if (stats.pictures_migrated > 0) details.push(`${stats.pictures_migrated} pictures migrated`);
      if (stats.images_migrated > 0) details.push(`${stats.images_migrated} images migrated`);
      if (stats.thumbnails_generated > 0) details.push(`${stats.thumbnails_generated} thumbnails generated`);
      if (mb_freed > 0) details.push(`${mb_freed} MB freed`);
      
      toast.success(
        `Migration complete: ${stats.matches_cleaned} matches processed${details.length ? '. ' + details.join(', ') : ''}`
      );
      
      // Refresh the report
      fetchBase64Report();
    } catch (error) {
      console.error("Failed to cleanup base64:", error);
      toast.error(error.response?.data?.detail || "Failed to cleanup base64 data");
    } finally {
      setCleaningBase64(false);
    }
  };

  // ─── System-wide Subscribe Bonus ────────────────────────────────────────
  const fetchSystemSubOffer = async () => {
    try {
      const { data } = await authAxios.get("/admin/system-subscribe-offer");
      setSystemSubOffer(data);
      setSystemSubBonusInput(String(data.bonus_tokens || 0));
      setSystemSubMaxUsesInput(String(data.max_uses ?? -1));
      setSystemSubPreview(data.plan_bonuses || { monthly: {}, yearly: {} });
    } catch (err) {
      console.error("Failed to fetch system subscribe offer:", err);
    }
  };

  const previewSystemSubBonus = (val) => {
    const base = parseInt(val, 10) || 0;
    if (base < 0) return;
    const yearly = {
      BRONZE: Math.floor(base * 10 / 75),
      SILVER: Math.floor(base * 25 / 75),
      GOLD: Math.floor(base * 50 / 75),
      PLATINUM: base,
    };
    const monthly = Object.fromEntries(Object.entries(yearly).map(([k, v]) => [k, Math.floor(v / 2)]));
    setSystemSubPreview({ monthly, yearly });
  };

  const handleSaveSystemSubOffer = async () => {
    const bonus = parseInt(systemSubBonusInput, 10);
    const maxUses = parseInt(systemSubMaxUsesInput, 10);
    if (Number.isNaN(bonus) || bonus < 0) {
      toast.error("Bonus tokens must be a non-negative integer");
      return;
    }
    if (Number.isNaN(maxUses) || (maxUses !== -1 && maxUses < 1)) {
      toast.error("Max uses must be -1 (unlimited) or a positive integer");
      return;
    }
    setSavingSystemSub(true);
    try {
      const { data } = await authAxios.put("/admin/system-subscribe-offer", {
        bonus_tokens: bonus,
        max_uses: maxUses,
        is_active: systemSubOffer.is_active,
        welcome_message: systemSubOffer.welcome_message || "",
      });
      setSystemSubOffer(data);
      setSystemSubPreview(data.plan_bonuses || { monthly: {}, yearly: {} });
      toast.success("System-wide Subscribe Bonus saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSavingSystemSub(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin w-8 h-8 border-4 border-divine-gold border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Timezone Display Settings Card */}
      <Card className="bg-divine-charcoal border-divine-gold/20">
        <CardHeader>
          <CardTitle className="text-divine-gold font-cinzel flex items-center gap-2">
            <Globe className="w-5 h-5" />
            Display Timezone
          </CardTitle>
          <CardDescription className="text-gray-400">
            Set your preferred timezone for viewing log timestamps. 
            All logs are stored in UTC - this only affects display.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <Label htmlFor="timezone" className="text-gray-200 flex items-center gap-2">
              <Clock className="w-4 h-4 text-divine-gold" />
              Your Timezone
            </Label>
            <Select
              value={userTimezone}
              onValueChange={handleSaveTimezone}
              disabled={savingTimezone}
            >
              <SelectTrigger className="bg-divine-black border-divine-gold/30 text-white w-full md:w-96">
                <SelectValue placeholder="Select timezone" />
              </SelectTrigger>
              <SelectContent className="bg-divine-charcoal border-divine-gold/30 max-h-80">
                {TIMEZONES.map((tz) => (
                  <SelectItem key={tz.value} value={tz.value} className="text-white">
                    {tz.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {savingTimezone && (
              <p className="text-sm text-divine-gold animate-pulse">Saving...</p>
            )}
          </div>
          
          {/* Current time preview */}
          <div className="p-4 bg-divine-black/50 rounded-lg border border-divine-gold/20">
            <p className="text-sm text-gray-400 mb-2">Current time in selected timezone:</p>
            <p className="text-lg text-white font-mono">
              {new Intl.DateTimeFormat("en-US", {
                timeZone: userTimezone,
                weekday: "short",
                year: "numeric",
                month: "short",
                day: "numeric",
                hour: "numeric",
                minute: "2-digit",
                second: "2-digit",
                hour12: true,
                timeZoneName: "short"
              }).format(new Date())}
            </p>
          </div>
          
          <p className="text-xs text-gray-500">
            This setting applies to all date/time displays in prompt logs, admin panels, and other timestamps.
            The underlying data remains in UTC for consistency.
          </p>
        </CardContent>
      </Card>

      {/* General Settings Card */}
      <Card className="bg-divine-charcoal border-divine-gold/20">
        <CardHeader>
          <CardTitle className="text-divine-gold font-cinzel">Global Settings</CardTitle>
          <CardDescription className="text-gray-400">
            Configure site-wide default settings
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Theme Selector */}
          <div className="space-y-3">
            <Label htmlFor="theme" className="text-gray-200">
              Default Theme
            </Label>
            <p className="text-sm text-gray-400">
              This setting controls the default theme for all users across the application
            </p>
            <Select
              value={settings.default_theme}
              onValueChange={(value) =>
                setSettings({ ...settings, default_theme: value })
              }
            >
              <SelectTrigger className="bg-divine-black border-divine-gold/30 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-divine-charcoal border-divine-gold/30">
                <SelectItem value="ancient_rome">Ancient Rome</SelectItem>
                <SelectItem value="agent_eros_1">Agent Eros 1</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Language Note */}
          <div className="p-4 bg-divine-black/50 border border-divine-gold/20 rounded-lg">
            <h4 className="text-sm font-semibold text-divine-gold mb-2">Language Settings</h4>
            <p className="text-sm text-gray-400">
              Language preferences are managed per-user. Users can select their preferred 
              language from their profile settings (English, French, Spanish, German, Italian).
            </p>
          </div>

          {/* Data Source Size Limit */}
          <div className="space-y-3">
            <Label htmlFor="data-limit" className="text-gray-200">
              Data Source Size Limit (characters)
            </Label>
            <p className="text-sm text-gray-400">
              Maximum size for pasted content and scraped data. Set to 0 for unlimited.
            </p>
            <Input
              id="data-limit"
              type="number"
              min="0"
              value={settings.data_source_max_size}
              onChange={(e) =>
                setSettings({ ...settings, data_source_max_size: parseInt(e.target.value) || 0 })
              }
              className="bg-divine-black border-divine-gold/30 text-white"
            />
            <p className="text-xs text-gray-500">
              Current: {settings.data_source_max_size === 0 ? 'Unlimited' : `${settings.data_source_max_size.toLocaleString()} characters`}
            </p>
          </div>

          {/* Save Button */}
          <div className="pt-4">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-divine-gold text-divine-black hover:bg-divine-gold/90"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? "Saving..." : "Save Settings"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* New Users & Subscribers Settings Card */}
      <Card className="bg-divine-charcoal border-divine-gold/20">
        <CardHeader>
          <CardTitle className="text-divine-gold font-cinzel flex items-center gap-2">
            <UserPlus className="w-5 h-5" />
            New Users and Subscribers
          </CardTitle>
          <CardDescription className="text-gray-400">
            Configure new user registration and signup bonuses
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Registration Mode */}
          <div className="space-y-3">
            <Label className="text-gray-200">Enable New User Registration</Label>
            <div className="space-y-2">
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="radio"
                  name="registration_mode"
                  value="yes"
                  checked={registrationSettings.registration_mode === "yes"}
                  onChange={(e) => setRegistrationSettings({...registrationSettings, registration_mode: e.target.value})}
                  className="w-4 h-4 accent-divine-gold"
                />
                <span className="text-white">Yes - Anyone can register</span>
              </label>
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="radio"
                  name="registration_mode"
                  value="no"
                  checked={registrationSettings.registration_mode === "no"}
                  onChange={(e) => setRegistrationSettings({...registrationSettings, registration_mode: e.target.value})}
                  className="w-4 h-4 accent-divine-gold"
                />
                <span className="text-white">No - Registration disabled</span>
              </label>
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="radio"
                  name="registration_mode"
                  value="email_limited"
                  checked={registrationSettings.registration_mode === "email_limited"}
                  onChange={(e) => setRegistrationSettings({...registrationSettings, registration_mode: e.target.value})}
                  className="w-4 h-4 accent-divine-gold"
                />
                <span className="text-white">Email Limited - Only approved emails</span>
              </label>
            </div>
          </div>

          {/* Email Limited Settings - Only show when email_limited is selected */}
          {registrationSettings.registration_mode === "email_limited" && (
            <div className="p-4 bg-divine-black/50 rounded-lg border border-amber-500/30 space-y-4">
              <h4 className="text-sm font-semibold text-amber-400 flex items-center gap-2">
                <Lock className="w-4 h-4" />
                Email Limited Settings
              </h4>
              
              <div className="space-y-2">
                <Label className="text-gray-200">Description</Label>
                <p className="text-xs text-gray-400">
                  This description is shown to users (e.g., "Closed-Beta", "Invite Only")
                </p>
                <Input
                  value={registrationSettings.email_limited_description}
                  onChange={(e) => setRegistrationSettings({...registrationSettings, email_limited_description: e.target.value})}
                  placeholder="Closed-Beta"
                  className="bg-divine-black border-divine-gold/30 text-white"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-gray-200">Approved Emails</Label>
                <p className="text-xs text-gray-400">
                  Enter email addresses that are allowed to register (comma-separated). These emails also serve as access codes.
                </p>
                <textarea
                  value={approvedEmailsText}
                  onChange={(e) => setApprovedEmailsText(e.target.value)}
                  placeholder="user1@example.com, user2@example.com"
                  rows={4}
                  className="w-full p-3 bg-divine-black border border-divine-gold/30 rounded-md text-white text-sm placeholder:text-gray-500"
                />
                <p className="text-xs text-gray-500">
                  {approvedEmailsText.split(",").filter(e => e.trim()).length} email(s) configured
                </p>
              </div>
            </div>
          )}

          {/* Signup Bonus Coins */}
          <div className="space-y-3">
            <Label className="text-gray-200 flex items-center gap-2">
              <Coins className="w-4 h-4 text-amber-400" />
              New User Bonus Coins
            </Label>
            <p className="text-sm text-gray-400">
              Amount of coins to award new users on signup. Deducted from bonus pool and credited to user.
            </p>
            <Input
              type="number"
              min="0"
              value={registrationSettings.signup_bonus_coins}
              onChange={(e) => setRegistrationSettings({...registrationSettings, signup_bonus_coins: parseInt(e.target.value) || 0})}
              placeholder="10000"
              className="bg-divine-black border-divine-gold/30 text-white w-48"
            />
            <p className="text-xs text-gray-500">
              Current: {registrationSettings.signup_bonus_coins > 0 
                ? `${registrationSettings.signup_bonus_coins.toLocaleString()} coins` 
                : 'No bonus'}
            </p>
          </div>

          {/* Verify Email Bonus */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Coins className="w-4 h-4 text-emerald-400" />
              Verify Email Bonus
            </Label>
            <p className="text-sm text-gray-400">
              Amount of coins awarded when a user verifies their email address. Deducted from their bonus pool.
            </p>
            <Input
              type="number"
              min="0"
              value={registrationSettings.verify_email_bonus}
              onChange={(e) => setRegistrationSettings({...registrationSettings, verify_email_bonus: parseInt(e.target.value) || 0})}
              placeholder="500"
              className="bg-divine-black border-divine-gold/30 text-white w-48"
              data-testid="verify-email-bonus-input"
            />
            <p className="text-xs text-gray-500">
              Current: {registrationSettings.verify_email_bonus > 0 
                ? `${registrationSettings.verify_email_bonus.toLocaleString()} coins` 
                : 'No bonus'}
            </p>
          </div>

          {/* Max Affiliate Code Bonus */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Coins className="w-4 h-4 text-amber-400" />
              Max Affiliate Code Bonus Coin Allocation
            </Label>
            <p className="text-sm text-gray-400">
              Maximum bonus tokens a user can assign to an affiliate code. Also limited to 20% of the user's coin balance.
            </p>
            <Input
              type="number"
              min="0"
              value={registrationSettings.max_affiliate_bonus}
              onChange={(e) => setRegistrationSettings({...registrationSettings, max_affiliate_bonus: parseInt(e.target.value) || 0})}
              placeholder="10000"
              className="bg-divine-black border-divine-gold/30 text-white w-48"
              data-testid="max-affiliate-bonus-input"
            />
            <p className="text-xs text-gray-500">
              Current: {registrationSettings.max_affiliate_bonus > 0 
                ? `${registrationSettings.max_affiliate_bonus.toLocaleString()} coins` 
                : 'No limit'}
            </p>
          </div>

          {/* Beta T&C — version + force update toggle */}
          <div className="pt-4 border-t border-divine-gold/10 mt-4">
            <h4 className="text-white font-semibold text-sm mb-3">Beta Terms & Conditions</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-white/80 text-xs">Latest T&C Version</Label>
                <Input
                  type="text"
                  value={registrationSettings.latest_tc_version || "1.0"}
                  onChange={(e) => setRegistrationSettings({...registrationSettings, latest_tc_version: e.target.value})}
                  className="bg-divine-black border-divine-gold/30 text-white mt-1"
                  data-testid="latest-tc-version-input"
                  placeholder="e.g. 1.0"
                />
                <p className="text-white/40 text-xs mt-1">
                  Version tag stored on new acceptances. Bump this AND update the T&C text in
                  <code className="mx-1 px-1 bg-white/10 rounded">backend/services/tc_service.py</code>
                  when you change the agreement.
                </p>
              </div>
              <div>
                <Label className="text-white/80 text-xs">Force T&C Update Check on all users?</Label>
                <div className="flex items-center gap-2 mt-2">
                  <select
                    value={registrationSettings.force_tc_update ? "yes" : "no"}
                    onChange={(e) => setRegistrationSettings({...registrationSettings, force_tc_update: e.target.value === "yes"})}
                    className="bg-divine-black border border-divine-gold/30 text-white rounded px-3 py-2 text-sm"
                    data-testid="force-tc-update-select"
                  >
                    <option value="no">No — only new users prompted</option>
                    <option value="yes">Yes — reprompt everyone with older version</option>
                  </select>
                </div>
                <p className="text-white/40 text-xs mt-1">
                  When Yes: any user whose accepted version differs from
                  <code className="mx-1 px-1 bg-white/10 rounded">latest_tc_version</code>
                  will be redirected to /beta-terms on their next login.
                </p>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="pt-4">
            <Button
              onClick={handleSaveRegistrationSettings}
              disabled={savingRegistration}
              className="bg-divine-gold text-divine-black hover:bg-divine-gold/90"
            >
              <Save className="h-4 w-4 mr-2" />
              {savingRegistration ? "Saving..." : "Save Registration Settings"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* System-wide Subscribe Bonus Card */}
      <Card className="bg-divine-charcoal border-divine-gold/20" data-testid="system-sub-offer-card">
        <CardHeader>
          <CardTitle className="text-divine-gold font-cinzel flex items-center gap-2">
            <Gift className="w-5 h-5" />
            System-wide Subscribe Bonus
          </CardTitle>
          <CardDescription className="text-gray-400">
            Set a platform-wide bonus that new subscribers receive on top of any sponsor
            offer. When both apply, whichever is higher is shown to the user. Bonus coins
            are debited from the platform&apos;s bonus pool.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Active toggle */}
          <div className="flex items-center justify-between p-4 bg-divine-black/50 rounded-lg border border-divine-gold/20">
            <div className="space-y-1 pr-4">
              <Label className="text-gray-200">Active</Label>
              <p className="text-sm text-gray-400">
                When ON, subscribers see this offer on /plans unless their sponsor is offering more.
              </p>
            </div>
            <Switch
              checked={systemSubOffer.is_active}
              onCheckedChange={(checked) => setSystemSubOffer({ ...systemSubOffer, is_active: checked })}
              data-testid="system-sub-active-toggle"
            />
          </div>

          {/* Base bonus (Platinum yearly) */}
          <div className="space-y-2">
            <Label className="text-gray-200 flex items-center gap-2">
              <Coins className="w-4 h-4 text-amber-400" />
              Base Bonus Coins (Platinum Yearly cap)
            </Label>
            <p className="text-sm text-gray-400">
              Maximum bonus payout — awarded to a new Platinum Yearly subscriber. Lower tiers
              and Monthly cycles scale down automatically (see preview below). Max 100,000.
            </p>
            <Input
              type="text"
              inputMode="numeric"
              value={systemSubBonusInput}
              onChange={(e) => {
                const clean = e.target.value.replace(/[^0-9]/g, "");
                setSystemSubBonusInput(clean);
                previewSystemSubBonus(clean);
              }}
              placeholder="0"
              className="bg-divine-black border-divine-gold/30 text-white w-48"
              data-testid="system-sub-bonus-input"
            />
          </div>

          {/* Max uses */}
          <div className="space-y-2">
            <Label className="text-gray-200 flex items-center gap-2">
              <InfinityIcon className="w-4 h-4 text-cyan-400" />
              Max Uses
            </Label>
            <p className="text-sm text-gray-400">
              Number of new subscribers that can redeem this offer. Enter <code className="mx-1 px-1 bg-white/10 rounded">-1</code> for unlimited.
            </p>
            <Input
              type="text"
              inputMode="numeric"
              value={systemSubMaxUsesInput}
              onChange={(e) => {
                const clean = e.target.value.replace(/[^0-9-]/g, "");
                setSystemSubMaxUsesInput(clean);
              }}
              placeholder="-1"
              className="bg-divine-black border-divine-gold/30 text-white w-48"
              data-testid="system-sub-max-uses-input"
            />
            <p className="text-xs text-gray-500">
              {systemSubOffer.max_uses === -1
                ? "Unlimited"
                : `${systemSubOffer.use_count} / ${systemSubOffer.max_uses} used`}
            </p>
          </div>

          {/* Welcome message */}
          <div className="space-y-2">
            <Label className="text-gray-200">Welcome Message (optional)</Label>
            <textarea
              value={systemSubOffer.welcome_message || ""}
              onChange={(e) => setSystemSubOffer({ ...systemSubOffer, welcome_message: e.target.value.slice(0, 500) })}
              placeholder="Optional message shown alongside the bonus (unused for now — reserved for future banner copy)"
              rows={3}
              className="w-full p-3 bg-divine-black border border-divine-gold/30 rounded-md text-white text-sm placeholder:text-gray-500"
              data-testid="system-sub-welcome-input"
            />
          </div>

          {/* Per-tier / cycle preview */}
          <div className="p-4 bg-divine-black/50 rounded-lg border border-divine-gold/20">
            <h4 className="text-sm font-semibold text-divine-gold mb-3">Per-tier payout preview</h4>
            <div className="grid grid-cols-5 gap-2 text-xs">
              <div className="text-gray-400"></div>
              <div className="text-center text-gray-400">Bronze</div>
              <div className="text-center text-gray-400">Silver</div>
              <div className="text-center text-gray-400">Gold</div>
              <div className="text-center text-gray-400">Platinum</div>

              <div className="text-gray-400 self-center">Monthly</div>
              {["BRONZE", "SILVER", "GOLD", "PLATINUM"].map((t) => (
                <div key={`m-${t}`} className="text-center font-mono text-white/80" data-testid={`system-sub-preview-monthly-${t}`}>
                  {(systemSubPreview.monthly?.[t] || 0).toLocaleString()}
                </div>
              ))}

              <div className="text-gray-400 self-center">Yearly</div>
              {["BRONZE", "SILVER", "GOLD", "PLATINUM"].map((t) => (
                <div key={`y-${t}`} className="text-center font-mono text-amber-300" data-testid={`system-sub-preview-yearly-${t}`}>
                  {(systemSubPreview.yearly?.[t] || 0).toLocaleString()}
                </div>
              ))}
            </div>
          </div>

          {/* Save button */}
          <div className="pt-2">
            <Button
              onClick={handleSaveSystemSubOffer}
              disabled={savingSystemSub}
              className="bg-divine-gold text-divine-black hover:bg-divine-gold/90"
              data-testid="system-sub-save-btn"
            >
              <Save className="h-4 w-4 mr-2" />
              {savingSystemSub ? "Saving..." : "Save System Bonus"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Called LLM Prompt outputs Card */}
      <Card className="bg-divine-charcoal border-divine-gold/20" data-testid="called-llm-prompt-outputs-card">
        <CardHeader>
          <CardTitle className="text-divine-gold font-cinzel flex items-center gap-2">
            <Database className="w-5 h-5" />
            Called LLM Prompt outputs
          </CardTitle>
          <CardDescription className="text-gray-400">
            Controls the size and shape of programmatically-generated context
            that gets injected into downstream LLM calls (CORE_*_DATA,
            STYLE_CARDs).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">

          {/* Minimal CORE_*_DATA toggle */}
          <div className="flex items-center justify-between p-4 bg-divine-black/50 rounded-lg border border-divine-gold/20">
            <div className="space-y-1 pr-4">
              <Label className="text-gray-200">Minimal MATCH/USER CORE DATA</Label>
              <p className="text-sm text-gray-400">
                When ON, CORE_USER_DATA and CORE_MATCH_DATA drop entire
                sections whose fields are all empty. Reduces input tokens to
                /eroscoach noticeably for new or sparse profiles. Toggling
                this lazily invalidates every cached row in db.data_sources
                (titles CORE_MATCH_DATA / CORE_USER_DATA) so the next call
                rebuilds with the new mode.
              </p>
            </div>
            <Switch
              checked={settings.minimal_core_data}
              onCheckedChange={(checked) =>
                setSettings({ ...settings, minimal_core_data: checked })
              }
              data-testid="minimal-core-data-toggle"
            />
          </div>

          {/* Style Card max_token_size */}
          <div className="p-4 bg-divine-black/50 rounded-lg border border-divine-gold/20 space-y-3">
            <div className="space-y-1">
              <Label className="text-gray-200">Set STYLE CARD max token size</Label>
              <p className="text-sm text-gray-400">
                Soft budget injected into the style_card_full prompt
                (substituted at runtime via the <code>{"{max_token_size}"}</code> placeholder).
                The LLM hard cap auto-scales to 20% above this value so
                light overruns don't truncate the card mid-XML-tag. The
                prompt tells the model to compress gracefully (tighten
                prose, then drop low-value content) rather than truncate.
                A <code>finish_reason=length</code> from the LLM raises
                a 500 to prevent persisting malformed cards.
                Default 5000.
              </p>
            </div>
            <Input
              type="number"
              min={2000}
              max={12000}
              step={500}
              value={settings.style_card_max_token_size}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  style_card_max_token_size: parseInt(e.target.value) || 5000,
                })
              }
              className="bg-divine-black border-white/20 text-white max-w-xs"
              data-testid="style-card-max-token-size-input"
            />
            <p className="text-xs text-gray-500">
              Bounds: 2000 — 12000. Step 500.
            </p>
          </div>

        </CardContent>
      </Card>

      {/* Prompt Logging Card */}
      <Card className="bg-divine-charcoal border-divine-gold/20">
        <CardHeader>
          <CardTitle className="text-divine-gold font-cinzel flex items-center gap-2">
            <Database className="w-5 h-5" />
            Prompt Logging (Langfuse)
          </CardTitle>
          <CardDescription className="text-gray-400">
            Control prompt logging and manage stored logs
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Toggle Prompt Logging */}
          <div className="flex items-center justify-between p-4 bg-divine-black/50 rounded-lg border border-divine-gold/20">
            <div className="space-y-1">
              <Label className="text-gray-200">Enable Prompt Logging</Label>
              <p className="text-sm text-gray-400">
                When enabled, full prompts and responses are logged to Langfuse for debugging.
                Token tracking continues regardless of this setting.
              </p>
            </div>
            <Switch
              checked={settings.prompt_logging_enabled}
              onCheckedChange={(checked) =>
                setSettings({ ...settings, prompt_logging_enabled: checked })
              }
            />
          </div>

          {/* Prompt Log Stats */}
          {promptLogStats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-3 bg-divine-black/50 rounded-lg border border-white/10">
                <p className="text-xs text-gray-400">Total Logs</p>
                <p className="text-lg font-bold text-white">{promptLogStats.total_logs?.toLocaleString() || 0}</p>
              </div>
              <div className="p-3 bg-divine-black/50 rounded-lg border border-white/10">
                <p className="text-xs text-gray-400">Input Tokens</p>
                <p className="text-lg font-bold text-white">{promptLogStats.total_input_tokens?.toLocaleString() || 0}</p>
              </div>
              <div className="p-3 bg-divine-black/50 rounded-lg border border-white/10">
                <p className="text-xs text-gray-400">Output Tokens</p>
                <p className="text-lg font-bold text-white">{promptLogStats.total_output_tokens?.toLocaleString() || 0}</p>
              </div>
              <div className="p-3 bg-divine-black/50 rounded-lg border border-white/10">
                <p className="text-xs text-gray-400">Date Range</p>
                <p className="text-xs text-white">
                  {promptLogStats.oldest_log ? new Date(promptLogStats.oldest_log).toLocaleDateString() : 'N/A'} - {promptLogStats.newest_log ? new Date(promptLogStats.newest_log).toLocaleDateString() : 'N/A'}
                </p>
              </div>
            </div>
          )}

          {/* Clear Prompt Logs */}
          <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg space-y-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              <h4 className="text-red-400 font-medium">Clear Prompt Logs</h4>
            </div>
            
            <div className="space-y-3">
              <Label className="text-gray-200">Clear Type</Label>
              <Select value={clearType} onValueChange={setClearType}>
                <SelectTrigger className="bg-divine-black border-white/20 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-divine-charcoal border-white/20">
                  <SelectItem value="all">All Data</SelectItem>
                  <SelectItem value="date_range">By Date Range</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {clearType === "date_range" && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-200 flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> Start Date
                  </Label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="bg-divine-black border-white/20 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-200 flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> End Date
                  </Label>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="bg-divine-black border-white/20 text-white"
                  />
                </div>
              </div>
            )}

            <Button
              variant="destructive"
              onClick={handleClearPromptLogs}
              disabled={clearing}
              className="bg-red-600 hover:bg-red-700"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              {clearing ? "Clearing..." : clearType === "all" ? "Clear All Logs" : "Clear Date Range"}
            </Button>
          </div>

          {/* Save Button for Logging Settings */}
          <div className="pt-4">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-divine-gold text-divine-black hover:bg-divine-gold/90"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? "Saving..." : "Save Logging Settings"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Database Maintenance Card */}
      <Card className="bg-divine-charcoal border-divine-gold/20">
        <CardHeader>
          <CardTitle className="text-divine-gold font-cinzel flex items-center gap-2">
            <HardDrive className="w-5 h-5" />
            Database Maintenance
          </CardTitle>
          <CardDescription className="text-gray-400">
            Clean up legacy data to keep the database light and responsive
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Base64 Cleanup Section */}
          <div className="p-4 bg-divine-black/50 rounded-lg border border-divine-gold/20 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-white font-medium flex items-center gap-2">
                  <FileSearch className="w-4 h-4 text-divine-gold" />
                  Base64 Image Migration & Cleanup
                </h4>
                <p className="text-sm text-gray-400 mt-1">
                  Migrate legacy base64 images to file storage and generate thumbnails.
                  All images will be converted to files with proper thumbnails for display.
                </p>
              </div>
            </div>

            {/* Report Section */}
            <div className="flex gap-3">
              <Button
                onClick={fetchBase64Report}
                disabled={loadingReport}
                variant="outline"
                className="border-divine-gold/30 text-divine-gold hover:bg-divine-gold/10"
              >
                {loadingReport ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Scanning...
                  </>
                ) : (
                  <>
                    <FileSearch className="w-4 h-4 mr-2" />
                    Scan for Base64 Data
                  </>
                )}
              </Button>
              
              {base64Report && (
                <Button
                  onClick={handleCleanupBase64}
                  disabled={cleaningBase64 || base64Report.total_base64_size_bytes === 0}
                  variant="destructive"
                  className="bg-red-600 hover:bg-red-700"
                >
                  {cleaningBase64 ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Migrating...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Migrate & Cleanup
                    </>
                  )}
                </Button>
              )}
            </div>

            {/* Report Results */}
            {base64Report && (
              <div className="mt-4 p-4 bg-divine-black rounded-lg border border-white/10">
                <h5 className="text-sm font-medium text-white mb-3">Scan Results</h5>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-xs text-gray-400">Total Matches</p>
                    <p className="text-lg font-bold text-white">{base64Report.total_matches}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">With Base64 Picture</p>
                    <p className="text-lg font-bold text-yellow-400">{base64Report.matches_with_base64_picture}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">With Base64 Images</p>
                    <p className="text-lg font-bold text-yellow-400">{base64Report.matches_with_base64_images}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Total Size</p>
                    <p className="text-lg font-bold text-red-400">{base64Report.total_base64_size_mb} MB</p>
                  </div>
                </div>
                
                {base64Report.affected_matches?.length > 0 && (
                  <div className="mt-4">
                    <p className="text-xs text-gray-400 mb-2">Affected Matches:</p>
                    <div className="max-h-32 overflow-y-auto space-y-1">
                      {base64Report.affected_matches.map((match) => (
                        <div key={match.match_id} className="flex justify-between text-sm">
                          <span className="text-gray-300">{match.name}</span>
                          <span className="text-yellow-400">{match.base64_size_kb} KB</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {base64Report.total_base64_size_bytes === 0 && (
                  <p className="text-sm text-green-400 mt-2">
                    ✓ No legacy base64 data found. Database is clean!
                  </p>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <MigrationsCard />
    </div>
  );
}
